import React, { useRef } from "react";

import { customIcons, icons } from "~/ui";
import { ACCEPTED_IMAGE_FORMATS, tw } from "~/utils";

type FileUploadProps = {
  label: string;
  name: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
  required?: boolean;
  error?: string;
  className?: string;
};

export const FileUpload = ({
  label,
  name,
  file,
  onFileChange,
  required,
  error,
  className,
}: FileUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputId = `file-upload-${name}`;
  const errorId = `${inputId}-error`;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    onFileChange(selectedFile);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      const fileExtension = `.${droppedFile.name.split(".").pop()?.toLowerCase()}`;
      const acceptedFormats = ACCEPTED_IMAGE_FORMATS.toLowerCase().split(",");
      if (acceptedFormats.includes(fileExtension)) {
        onFileChange(droppedFile);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = () => {
    onFileChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={tw("space-y-2", className)}>
      <label htmlFor={inputId} className="block text-sm text-gray-700">
        {label}

        {required && (
          <span aria-hidden="true" className="ml-1">
            *
          </span>
        )}
      </label>

      <input
        ref={fileInputRef}
        id={inputId}
        name={name}
        type="file"
        accept={ACCEPTED_IMAGE_FORMATS}
        onChange={handleFileChange}
        aria-required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        className="peer sr-only"
      />

      {!file ? (
        <div
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className={tw(
            "hover:border-indigo hover:bg-indigo/5 peer-focus-visible:ring-indigo cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-6 text-center transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2",
            error && "border-red-500",
          )}
        >
          <icons.outline.PhotoIcon className="mx-auto mb-2 size-8 text-gray-400" />
          <p className="text-xs text-gray-600">Upload or take a photo</p>
        </div>
      ) : (
        <div className="peer-focus-visible:ring-indigo rounded-lg border border-gray-300 p-4 peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2">
          <div className="flex flex-col items-center justify-between gap-1.5">
            <customIcons.CheckCircle className="text-success size-8" />
            <div className="flex items-center gap-3 text-center">
              <p className="text-sm text-gray-900">{file.name}</p>
            </div>

            <button
              type="button"
              onClick={removeFile}
              className="flex items-center gap-1 transition-colors"
            >
              <icons.outline.TrashIcon className="size-4" />
              <p className="text-sm text-gray-600">Clear upload</p>
            </button>
          </div>
        </div>
      )}

      {error && (
        <p
          id={errorId}
          className="flex items-center gap-1 text-sm text-red-600"
        >
          <icons.outline.ExclamationCircleIcon className="size-4" />
          {error}
        </p>
      )}
    </div>
  );
};
